import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { toast } from 'react-hot-toast';
import { Mail, Save, Eye } from 'lucide-react';

export default function EmailTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [formData, setFormData] = useState({
    subject_template: '',
    html_template: '',
    text_template: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    try {
      const { data, error } = await supabase
        ?.from('email_templates')
        ?.select('*')
        ?.order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast?.error('Failed to load email templates');
    }
  }

  function handleSelectTemplate(template) {
    setSelectedTemplate(template);
    setFormData({
      subject_template: template?.subject_template || '',
      html_template: template?.html_template || '',
      text_template: template?.text_template || ''
    });
    setEditing(false);
  }

  async function handleSave() {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('email_templates')
        ?.update({
          subject_template: formData?.subject_template,
          html_template: formData?.html_template,
          text_template: formData?.text_template,
          version: (selectedTemplate?.version || 1) + 1,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', selectedTemplate?.id);

      if (error) throw error;

      toast?.success('Template saved successfully');
      setEditing(false);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast?.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  }

  function renderPreview() {
    const sampleVariables = {
      userName: 'John Doe',
      caseTitle: 'Sample Case #12345',
      downloadLink: 'https://firecaseiq.com/report',
      planName: 'Professional Plan',
      amount: '99.00',
      code: '123456'
    };

    let previewHtml = formData?.html_template;
    Object.keys(sampleVariables)?.forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewHtml = previewHtml?.replace(regex, sampleVariables?.[key]);
    });

    return previewHtml;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Mail className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-900">Email Template Editor</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Email Templates</h3>
          <div className="space-y-2">
            {templates?.map(template => (
              <button
                key={template?.id}
                onClick={() => handleSelectTemplate(template)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedTemplate?.id === template?.id
                    ? 'bg-purple-50 border-purple-300 text-purple-900' :'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">{template?.template_name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {template?.email_type?.replace(/_/g, ' ')}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  v{template?.version} • {template?.is_active ? 'Active' : 'Inactive'}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          {selectedTemplate ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {selectedTemplate?.template_name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedTemplate?.description}
                  </p>
                </div>
                <div className="flex space-x-2">
                  {editing ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreview(!preview)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {preview ? 'Hide' : 'Preview'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setEditing(true)}
                      >
                        Edit Template
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Available Variables */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900">Available Variables:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedTemplate?.available_variables?.map(variable => (
                    <code
                      key={variable}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                    >
                      {`{{${variable}}}`}
                    </code>
                  ))}
                </div>
              </div>

              {preview ? (
                /* Preview Mode */
                (<div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Preview
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-900">
                      {formData?.subject_template}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Preview
                    </label>
                    <div
                      className="bg-white border border-gray-200 rounded-lg p-4 overflow-auto max-h-[600px]"
                      dangerouslySetInnerHTML={{ __html: renderPreview() }}
                    />
                  </div>
                </div>)
              ) : (
                /* Edit Mode */
                (<div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Template
                    </label>
                    <Input
                      value={formData?.subject_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e?.target?.value }))}
                      disabled={!editing}
                      placeholder="Email subject with {{variables}}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Template
                    </label>
                    <textarea
                      value={formData?.html_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, html_template: e?.target?.value }))}
                      disabled={!editing}
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="HTML email template with {{variables}}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plain Text Template (Optional)
                    </label>
                    <textarea
                      value={formData?.text_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, text_template: e?.target?.value }))}
                      disabled={!editing}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Plain text fallback"
                    />
                  </div>
                </div>)
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Mail className="w-16 h-16 mb-4" />
              <p>Select a template to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
